import { createContext, useContext, useId, forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * Field — la envoltura de un campo: etiqueta, control, ayuda y error.
 *
 * Cuatro decisiones que no son estéticas:
 *
 * 1. La etiqueta va SIEMPRE encima, nunca dentro del campo. Una etiqueta que
 *    vive en el placeholder desaparece justo cuando empiezas a escribir, que es
 *    cuando hace falta para comprobar que estás en el campo correcto.
 *
 * 2. Se marca lo OPCIONAL, no lo obligatorio. En estos formularios casi todo es
 *    obligatorio: un asterisco en catorce de quince campos es ruido, y el que
 *    importa —el que no lo lleva— se pierde entre ellos. Marcar la excepción
 *    dice más con menos tinta.
 *
 * 3. El error SUSTITUYE a la ayuda, no se apila debajo. Apilar mueve todo lo
 *    que hay más abajo en el formulario justo cuando el usuario está mirando
 *    otra cosa.
 *
 * 4. El `aria-describedby` se cablea aquí y no en cada pantalla. Es la parte
 *    que siempre se olvida, y sin ella el lector de pantalla lee el campo sin
 *    su ayuda ni su error.
 */

type CtxCampo = { id: string; idAyuda: string; invalido: boolean };
const Ctx = createContext<CtxCampo | null>(null);

function useCampo() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('Input/Select/Textarea tienen que ir dentro de <Field>');
  return ctx;
}

export function Field({
  label, ayuda, error, opcional, children, className, id: idExterno,
}: {
  /** Texto de la etiqueta. Admite nodos para poder colgarle una marca —«sin
   *  dato», por ejemplo— sin que cada pantalla se invente su propia fila de
   *  etiqueta por encima del campo. Sigue aceptando una cadena suelta. */
  label: React.ReactNode;
  /** Qué se espera en el campo. Desaparece cuando hay error. */
  ayuda?: string;
  /** Qué está mal y cómo arreglarlo — no «campo inválido». */
  error?: string;
  opcional?: boolean;
  children: React.ReactNode;
  className?: string;
  /** Id propio para el control, cuando algo de fuera necesita enfocarlo — por
   *  ejemplo, llevar el foco al primer campo obligatorio que falta. Si no se
   *  pasa, se genera uno.
   *
   *  Va aquí y NO en el `<Input>` a propósito: los controles reciben su id del
   *  contexto DESPUÉS de esparcir sus props, así que un `id` puesto en el input
   *  ganaba y dejaba al `<label htmlFor>` apuntando a un elemento que ya no
   *  existía. La etiqueta seguía viéndose; lo que se perdía era que pulsarla
   *  enfocara el campo, y que el lector de pantalla lo leyera con su nombre. */
  id?: string;
}) {
  const generado = useId();
  const id = idExterno ?? generado;
  const idAyuda = `${id}-ayuda`;
  const invalido = Boolean(error);

  return (
    <Ctx.Provider value={{ id, idAyuda, invalido }}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label htmlFor={id} className="text-3 font-semibold text-ink">
          {label}
          {opcional && (
            <span className="ml-1.5 text-2 font-normal text-muted">opcional</span>
          )}
        </label>

        {children}

        {(error || ayuda) && (
          <p
            id={idAyuda}
            className={cn('text-2 leading-snug', error ? 'text-danger-ink' : 'text-muted')}
            // El error se anuncia al aparecer; la ayuda no interrumpe.
            role={error ? 'alert' : undefined}
          >
            {error ?? ayuda}
          </p>
        )}
      </div>
    </Ctx.Provider>
  );
}

/* El borde del campo tiene token propio (`--borde-campo`, #79828E). No reusa
   `--gray-300`: ese también dibuja separadores, donde un gris fuerte sería
   ruido, y con él los campos daban 1,24 de contraste contra el panel —o sea,
   no se veía dónde había que escribir. Medido el 2026-08-04. */
const control =
  'w-full rounded-xs border bg-surface px-3 py-2 text-3 text-ink ' +
  // 44 px en pantalla de dedo. El bloque `pointer: coarse` de
  // design-system.css NO llega hasta aquí: nombra `.btn`, `.icon-btn`,
  // `.nav-item`… —clases del vanilla que la capa React no lleva—. Medido el
  // 2026-09-07 con puntero grueso: TODOS los campos de texto de las dos
  // pantallas migradas daban 34 px, incluidos los buscadores de las barras de
  // filtro. `ui/Button` y `ui/Tabs` ya se corregían por su cuenta; los campos
  // no tenían quien lo hiciera.
  'pointer-coarse:min-h-11 ' +
  'border-field-border placeholder:text-muted ' +
  'transition-[border-color,box-shadow] duration-150 ' +
  'focus:outline-none focus:border-brand-ink focus:ring-2 focus:ring-accent/35 ' +
  'disabled:bg-sunken disabled:text-muted disabled:cursor-not-allowed ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-danger/25';

/**
 * Las clases del control, para los pocos sitios donde el campo **ya tiene su
 * propia etiqueta** y meterlo en un `<Field>` pintaría una segunda.
 *
 * Son tres, y los tres son legítimos: el buscador de una barra de filtros (se
 * rotula con un `sr-only` junto a su icono), las dos mitades de un rango de
 * fechas (cada una lleva su `<label for>` y el grupo su `aria-labelledby`) y
 * los ajustes compactos del constructor de tipos.
 *
 * Fuera de esos casos, **el control va dentro de `<Field>`**: es lo que cablea
 * el `aria-describedby` con la ayuda y el error, que es la parte que siempre se
 * olvida. Por eso `Input`/`Select`/`Textarea` revientan si no encuentran el
 * contexto en vez de apañárselas en silencio.
 */
export const clasesDeControl = control;

/** El adorno del `<select>` nativo, para el mismo caso. */
export const adornoDeSelect = {
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%235C636A'%3E%3Cpath d='M4.5 6.5 8 10l3.5-3.5z'/%3E%3C/svg%3E\")",
  backgroundPosition: 'right 10px center',
  backgroundSize: '16px',
} as const;

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    const { id, idAyuda, invalido } = useCampo();
    return (
      <input
        ref={ref}
        id={id}
        aria-describedby={idAyuda}
        aria-invalid={invalido}
        className={cn(control, className)}
        {...props}
      />
    );
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 4, ...props }, ref) {
  const { id, idAyuda, invalido } = useCampo();
  return (
    <textarea
      ref={ref}
      id={id}
      rows={rows}
      aria-describedby={idAyuda}
      aria-invalid={invalido}
      className={cn(control, 'resize-y min-h-24', className)}
      {...props}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...props }, ref) {
  const { id, idAyuda, invalido } = useCampo();
  return (
    <select
      ref={ref}
      id={id}
      aria-describedby={idAyuda}
      aria-invalid={invalido}
      // El `<select>` nativo se conserva a propósito: en móvil abre la rueda
      // del sistema operativo, que es más rápida y más accesible que cualquier
      // desplegable que dibujemos nosotros. Se sustituirá solo donde haga falta
      // buscar dentro de muchas opciones.
      className={cn(control, 'appearance-none bg-no-repeat pr-9', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%235C636A'%3E%3Cpath d='M4.5 6.5 8 10l3.5-3.5z'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 10px center',
        backgroundSize: '16px',
      }}
      {...props}
    >
      {children}
    </select>
  );
});

/* ── Controles sueltos ───────────────────────────────────────────────────
 *
 * `Input`/`Select`/`Textarea` **revientan** fuera de `<Field>`, y eso está
 * bien: es lo que impide que un campo llegue a producción sin etiqueta ni
 * `aria-describedby`. Pero el portal tiene sitios donde el `<Field>` sobra
 * porque el control ya tiene su nombre propio: el buscador de una barra de
 * filtros, las casillas compactas de un constructor de columnas, las dos
 * mitades de un rango de fechas.
 *
 * Hasta el 2026-09-24 la salida era `clasesDeControl` con un `<input>` a pelo,
 * y el resultado fue el previsible: al migrar tres módulos seguidos se
 * escribieron cuarenta `<Input aria-label="…">` fuera de `<Field>` y **todos
 * lanzaban al pintarse**. Compilaban, pasaban las pruebas, y la pantalla
 * entera caía en el límite de errores. Se vio abriendo el portal, no leyendo
 * el código.
 *
 * Estas tres variantes son ese caso, con nombre. Exigen `aria-label` en el
 * tipo: un control suelto SIN etiqueta no es un caso legítimo, es un descuido.
 */

type ConEtiqueta<T> = T & { 'aria-label': string };

export const InputSuelto = forwardRef<
  HTMLInputElement,
  ConEtiqueta<React.InputHTMLAttributes<HTMLInputElement>>
>(function InputSuelto({ className, ...props }, ref) {
  return <input ref={ref} className={cn(control, className)} {...props} />;
});

export const TextareaSuelto = forwardRef<
  HTMLTextAreaElement,
  ConEtiqueta<React.TextareaHTMLAttributes<HTMLTextAreaElement>>
>(function TextareaSuelto({ className, rows = 3, ...props }, ref) {
  return (
    <textarea ref={ref} rows={rows} className={cn(control, 'min-h-24 resize-y', className)} {...props} />
  );
});

export const SelectSuelto = forwardRef<
  HTMLSelectElement,
  ConEtiqueta<React.SelectHTMLAttributes<HTMLSelectElement>>
>(function SelectSuelto({ className, children, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={cn(control, 'appearance-none bg-no-repeat pr-9', className)}
      style={adornoDeSelect}
      {...props}
    >
      {children}
    </select>
  );
});
